import { useState, useRef, useCallback } from 'react';
import type React from 'react';
import styled from '@emotion/styled';
import Body from 'storybookConfig/stories/molecules/Body';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import Flex from '@react-css/flex';

const MIN_THRESHOLD = 1000; // $1K
const MAX_THRESHOLD = 1000000; // $1M

// Define snap values for the slider
const SNAP_VALUES = [
  1000, // 1k
  10000, // 10k
  50000, // 50k
  100000, // 100k
  200000, // 200k
  300000, // 300k
  400000, // 400k
  500000, // 500k
  600000, // 600k
  700000, // 700k
  800000, // 800k
  900000, // 900k
  1000000, // 1m
];

const CustomSecondaryButton = styled(SecondaryButton)`
  border: 0;
  outline: 1px solid ${({ theme }) => theme.color.blue700};
`;

const OpenButton = styled(PrimaryButton)`
  background: linear-gradient(268deg, #228aff 0.14%, #2bba9a 100%);
  border: 0;
  &:hover {
    background: linear-gradient(268deg, #228aff 0.14%, #2bba9a 100%);
  }
`;

const HybridButton = styled(PrimaryButton)`
  background: linear-gradient(270.5deg, #6effde 0%, #12b08c 38%, #008668 100%);
  border: 0;
  &:hover {
    background: linear-gradient(270.5deg, #6effde 0%, #12b08c 38%, #008668 100%);
  }
`;

const CuratedButton = styled(PrimaryButton)`
  background: linear-gradient(268deg, #228aff 0.14%, #6659ff 100%);
  border: 0;
  &:hover {
    background: linear-gradient(268deg, #228aff 0.14%, #6659ff 100%);
  }
`;

const SliderContainer = styled.div`
  margin: 24px 0;
  position: relative;
  padding-top: 30px;
`;

const SliderTrack = styled.div`
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #6effde 0%, #2bba9a 100%);
  border-radius: 2px;
  position: relative;
  cursor: pointer;
`;

const SliderThumb = styled.div<{ position: number }>`
  width: 24px;
  height: 24px;
  background: white;
  border: 2px solid #333;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: ${(props) => props.position}%;
  transform: translate(-50%, -50%);
  cursor: grab;
  z-index: 2;

  &:active {
    cursor: grabbing;
  }
`;

const ThresholdPill = styled.div<{ position: number }>`
  position: absolute;
  top: 55px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 14px;
  white-space: nowrap;
  z-index: 3;
`;

const ButtonContainer = styled.div`
  margin-bottom: 24px;
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

interface RetailerAccessSliderProps {
  value: string;
  approvalThreshold?: number;
  onSliderChange: (value: string, threshold?: number) => void;
}

const RetailerAccessSlider: React.FC<RetailerAccessSliderProps> = ({
  value,
  approvalThreshold = MIN_THRESHOLD,
  onSliderChange,
}) => {
  const sliderRef = useRef<HTMLDivElement>(null);

  // `isDragging` is true when the user is actively dragging the slider.
  // It's used to prevent the component's internal state from being overwritten
  // by props during a drag operation, which would cause the slider to snap back.
  const [isDragging, setIsDragging] = useState(false);

  // Constants for slider zones
  const HYBRID_SNAP_THRESHOLD_LOWER = 1;
  const HYBRID_SNAP_THRESHOLD_UPPER = 99;

  // `calculatePositionFromProps` converts the `approvalThreshold` prop
  // into a percentage position for the slider thumb (only for hybrid mode).
  const calculatePositionFromProps = useCallback(() => {
    // For HYBRID, calculate position based on the logarithmic scale.
    const logMin = Math.log10(MIN_THRESHOLD);
    const logMax = Math.log10(MAX_THRESHOLD);

    // Normalize the log of the threshold to a 0-1 scale.
    const scale = (Math.log10(approvalThreshold) - logMin) / (logMax - logMin);

    // Map the 0-1 scale to the hybrid position range on the slider.
    const hybridRange = HYBRID_SNAP_THRESHOLD_UPPER - HYBRID_SNAP_THRESHOLD_LOWER;
    const scaledPosition = scale * hybridRange + HYBRID_SNAP_THRESHOLD_LOWER;

    return Math.min(
      Math.max(scaledPosition, HYBRID_SNAP_THRESHOLD_LOWER),
      HYBRID_SNAP_THRESHOLD_UPPER
    );
  }, [approvalThreshold]);

  // Find the closest snap value to a given threshold
  const findClosestSnapValue = (threshold: number): number => {
    return SNAP_VALUES.reduce((closest, current) => {
      return Math.abs(current - threshold) < Math.abs(closest - threshold) ? current : closest;
    });
  };

  // Calculate current position from props
  const position = calculatePositionFromProps();

  // `handlePositionUpdate` is the core logic that runs when the user interacts with the slider.
  // It takes a new position and updates the parent component's state.
  const handlePositionUpdate = useCallback(
    (newPosition: number) => {
      // Only handle hybrid mode - slider only appears when in hybrid mode
      const hybridRange = HYBRID_SNAP_THRESHOLD_UPPER - HYBRID_SNAP_THRESHOLD_LOWER;
      const positionInHybridRange = newPosition - HYBRID_SNAP_THRESHOLD_LOWER;

      // Normalize position to a 0-1 scale
      const scale = positionInHybridRange / hybridRange;

      // Convert from linear scale to log scale
      const logMin = Math.log10(MIN_THRESHOLD);
      const logMax = Math.log10(MAX_THRESHOLD);
      const logValue = scale * (logMax - logMin) + logMin;
      let newThreshold = Math.round(10 ** logValue);

      // Snap to exact threshold values at extremes
      if (newThreshold < MIN_THRESHOLD) {
        newThreshold = MIN_THRESHOLD;
      } else if (newThreshold > MAX_THRESHOLD) {
        newThreshold = MAX_THRESHOLD;
      }

      // Apply snapping to the closest predefined value
      const snappedThreshold = findClosestSnapValue(newThreshold);

      // Update state for hybrid mode
      onSliderChange('HYBRID', snappedThreshold);
    },
    [onSliderChange]
  );

  // `handleDrag` is called on mouse move. It updates the state directly.
  const handleDrag = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return;
      const rect = sliderRef.current.getBoundingClientRect();
      const newPosition = Math.min(Math.max(((clientX - rect.left) / rect.width) * 100, 0), 100);

      handlePositionUpdate(newPosition); // Update state
    },
    [handlePositionUpdate]
  );

  // Sets up event listeners when the user clicks/starts dragging.
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const handleMouseMove = (event: MouseEvent) => {
        handleDrag(event.clientX);
      };

      const handleMouseUp = () => {
        setIsDragging(false); // End of drag
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // Handle the initial click position
      handleDrag(e.clientX);
    },
    [handleDrag]
  );

  // To provide immediate feedback during a drag, we calculate the pill's
  // visibility and displayed value based on the current slider position,
  // rather than waiting for the parent component's state to update.
  const isHybridPosition =
    position > HYBRID_SNAP_THRESHOLD_LOWER && position < HYBRID_SNAP_THRESHOLD_UPPER;

  // The pill should be visible whenever the slider is in the hybrid range.
  let displayThreshold = approvalThreshold;

  if (isDragging && isHybridPosition) {
    // Calculate threshold from the current visual position for immediate display.
    const hybridRange = HYBRID_SNAP_THRESHOLD_UPPER - HYBRID_SNAP_THRESHOLD_LOWER;
    const positionInHybridRange = position - HYBRID_SNAP_THRESHOLD_LOWER;
    const scale = positionInHybridRange / hybridRange;
    const logMin = Math.log10(MIN_THRESHOLD);
    const logMax = Math.log10(MAX_THRESHOLD);
    const logValue = scale * (logMax - logMin) + logMin;
    const calculatedThreshold = Math.round(10 ** logValue);

    // Apply snapping to the display value as well for immediate feedback
    displayThreshold = findClosestSnapValue(calculatedThreshold);
  }

  return (
    <div>
      <ButtonContainer>
        <Flex gap="20px">
          {value === 'OPEN' ? (
            <OpenButton style={{ flex: 1 }}>Open</OpenButton>
          ) : (
            <CustomSecondaryButton
              style={{ flex: 1 }}
              onClick={() => {
                onSliderChange('OPEN');
              }}
            >
              Open
            </CustomSecondaryButton>
          )}
          {value === 'HYBRID' ? (
            <HybridButton style={{ flex: 1 }}>Hybrid</HybridButton>
          ) : (
            <CustomSecondaryButton
              style={{ flex: 1 }}
              onClick={() => {
                // Set to 10k when clicking Hybrid button
                onSliderChange('HYBRID', 10000);
              }}
            >
              Hybrid
            </CustomSecondaryButton>
          )}
          {value === 'CURATED' ? (
            <CuratedButton style={{ flex: 1 }}>Curated</CuratedButton>
          ) : (
            <CustomSecondaryButton
              style={{ flex: 1 }}
              onClick={() => {
                onSliderChange('CURATED');
              }}
            >
              Curated
            </CustomSecondaryButton>
          )}
        </Flex>
      </ButtonContainer>

      {/* Only show slider when in Hybrid mode */}
      {value === 'HYBRID' && (
        <SliderContainer>
          <ThresholdPill position={position}>${displayThreshold.toLocaleString()}</ThresholdPill>
          <SliderTrack ref={sliderRef} onMouseDown={handleMouseDown}>
            <SliderThumb position={position} />
          </SliderTrack>
          <SliderLabels>
            <Body variant="Body/Body Small" color="bodyTextSecondary" style={{ marginTop: '4px' }}>
              $1,000
            </Body>
            <Body variant="Body/Body Small" color="bodyTextSecondary" style={{ marginTop: '4px' }}>
              $1,000,000+
            </Body>
          </SliderLabels>
        </SliderContainer>
      )}
    </div>
  );
};

export default RetailerAccessSlider;
